export const TEXT_EXTENSIONS = new Set([
  ".txt",
  ".json",
  ".xml",
  ".md",
  ".markdown",
  ".log",
  ".yml",
  ".yaml",
  ".ini",
  ".conf",
  ".cfg",
  ".env",
  ".html",
  ".htm",
  ".css",
  ".js",
  ".mjs",
  ".cjs",
  ".ts",
  ".jsx",
  ".tsx",
  ".py",
  ".sh",
  ".bat",
  ".properties",
  ".sql",
  ".toml",
]);

export const IMAGE_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
  ".ico",
  ".avif",
  ".heic",
  ".heif",
  ".svg",
  ".dng",
]);

/** RAW photos (DNG etc.) — browsers cannot render these; use embedded JPEG preview. */
export const RAW_IMAGE_EXTENSIONS = new Set([
  ".dng",
  ".raw",
  ".cr2",
  ".nef",
  ".arw",
  ".orf",
  ".rw2",
]);

const RAW_IMAGE_MIMES = new Set(["image/x-adobe-dng", "image/dng"]);

const PDF_EXTENSIONS = new Set([".pdf"]);
const WORD_EXTENSIONS = new Set([".doc", ".docx", ".odt", ".rtf"]);
const EXCEL_EXTENSIONS = new Set([".xls", ".xlsx", ".ods", ".csv"]);
const POWERPOINT_EXTENSIONS = new Set([".ppt", ".pptx", ".odp"]);

export const PREVIEW_LIMITS = {
  text: 5 * 1024 * 1024,
  image: 100 * 1024 * 1024,
  mp3: 200 * 1024 * 1024,
  mp4: 1024 * 1024 * 1024,
};

const getExtension = (name = "") => {
  const lower = String(name).toLowerCase();
  const dot = lower.lastIndexOf(".");
  return dot >= 0 ? lower.slice(dot) : "";
};

export const isRawCloudImage = (file) => {
  const ext = getExtension(file?.name || "");
  const mime = (file?.mimeType || "").toLowerCase();
  return RAW_IMAGE_EXTENSIONS.has(ext) || RAW_IMAGE_MIMES.has(mime);
};

export const classifyCloudFile = (file) => {
  const name = file?.name || "";
  const mime = (file?.mimeType || "").toLowerCase();
  const ext = getExtension(name);

  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.has(ext)) return "image";
  if (ext === ".mp3" || mime === "audio/mpeg" || mime.startsWith("audio/")) return "mp3";
  if (
    [".mp4", ".mov", ".webm", ".m4v", ".mkv", ".avi"].includes(ext) ||
    mime.startsWith("video/")
  ) {
    return "mp4";
  }
  if (
    PDF_EXTENSIONS.has(ext) ||
    mime === "application/pdf"
  ) {
    return "pdf";
  }
  if (
    WORD_EXTENSIONS.has(ext) ||
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "word";
  }
  if (
    EXCEL_EXTENSIONS.has(ext) ||
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "excel";
  }
  if (
    POWERPOINT_EXTENSIONS.has(ext) ||
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ) {
    return "powerpoint";
  }
  if (
    TEXT_EXTENSIONS.has(ext) ||
    mime.startsWith("text/") ||
    mime === "application/json" ||
    mime === "application/xml" ||
    mime === "text/xml" ||
    mime === "application/javascript" ||
    mime === "application/x-yaml"
  ) {
    return "text";
  }
  return "other";
};

export const isPreviewableCloudFile = (file) => {
  const kind = classifyCloudFile(file);
  return ["image", "mp3", "mp4", "text"].includes(kind);
};

export const canPreviewCloudFile = (file) => {
  if (!isPreviewableCloudFile(file)) return false;
  const kind = classifyCloudFile(file);
  const size = Number(file?.size);
  if (!Number.isFinite(size) || size <= 0) return true;
  return size <= PREVIEW_LIMITS[kind];
};

export const isEditableTextFile = (file) => classifyCloudFile(file) === "text";
